import axiosInstance from '../components/api/axiosConfig';
import { getSafeRedirectPath } from './routePermissions';

// Decode a JWT on the client to check exp (no signature verification)
function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

function isJwtValid(token) {
  if (!token) return false;
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp > nowSeconds;
}

// Check if the session is valid and user is authenticated
export const checkSession = async () => {
  // First check if we have a user in storage (prefer sessionStorage for non-remember sessions)
  const sessionUser = sessionStorage.getItem('user');
  const localUser = localStorage.getItem('user');
  const storedUser = sessionUser || localUser;
  if (!storedUser) {
    return {
      isAuthenticated: false,
      error: 'No stored session'
    };
  }
  
  try {
    // Try to parse the stored user data first
    let userData = JSON.parse(storedUser);
    
    // If we have a valid JWT, trust it even if cookie session isn't present
    const tokenFromSession = sessionStorage.getItem('access_token');
    const tokenFromUser = userData?.access_token || userData?.access;
    const accessToken = tokenFromSession || tokenFromUser;
    if (isJwtValid(accessToken)) {
      // Ensure token is synced for interceptors
      sessionStorage.setItem('access_token', accessToken);
      return {
        isAuthenticated: true,
        user: userData
      };
    }
    
    try {
      // Verify the session with backend - explicitly ensure credentials are included
      const response = await axiosInstance.get('/api/session/', { withCredentials: true });
      
      // Update stored user data with latest from server
      userData = response.data;
      // Persist back to the same storage it came from
      if (sessionUser) {
        sessionStorage.setItem('user', JSON.stringify(userData));
      } else {
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (error) {
      // If API call fails with 404 (endpoint not found), we'll still use local data
      // but for authentication errors, clear localStorage
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        return {
          isAuthenticated: false,
          error: 'Session expired or invalid'
        };
      }
      
      // For other errors (like 404), we'll continue using the local data
      console.warn('Session check API error:', error.message);
      // Don't return early, continue using localStorage data
    }
    
    // If we got here, either the API call succeeded or failed with non-auth error
    // In both cases, we'll use the userData we have
    return {
      isAuthenticated: true,
      user: userData
    };
  } catch (error) {
    // Error parsing stored user data
    localStorage.removeItem('user');
    return {
      isAuthenticated: false,
      error: 'Invalid stored session data'
    };
  }
};

// Extract next parameter from URL or use default with permission checking
export const getRedirectPath = (location, userGroup = null) => {
  const searchParams = new URLSearchParams(location.search);
  const nextPath = searchParams.get('next');
  
  // Get user group from session storage if not provided
  const currentUserGroup = userGroup || sessionStorage.getItem('user_group');
  
  // Use the safe redirect path function that checks permissions
  return getSafeRedirectPath(currentUserGroup, nextPath);
};

// Navigate to login with next parameter
export const redirectToLogin = (navigate, currentPath) => {
  navigate(`/login?next=${encodeURIComponent(currentPath)}`);
};

// Helper to detect if a session has expired
export const isSessionExpired = (error) => {
  return error.response && (error.response.status === 401 || error.response.status === 403);
};

// Handle API responses with session expiry check
export const handleApiResponse = async (apiCall, onSessionExpired) => {
  try {
    const response = await apiCall();
    return {
      data: response.data,
      success: true
    };
  } catch (error) {
    if (isSessionExpired(error)) {
      if (onSessionExpired) {
        onSessionExpired();
      }
      return {
        success: false,
        sessionExpired: true,
        error: 'Your session has expired. Please login again.'
      };
    }
    
    return {
      success: false,
      error: error.response?.data || error.message || 'An error occurred'
    };
  }
};